// src/pages/Admin/HotelSlaPage.jsx
import React, { useEffect, useState } from "react";
import {
  Alert,
  Avatar,
  Badge,
  Button,
  Card,
  Col,
  Divider,
  Empty,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Space,
  Spin,
  Switch,
  Table,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import {
  CheckCircleOutlined,
  EditOutlined,
  ExclamationCircleOutlined,
  FileTextOutlined,
  InfoCircleOutlined,
  ReloadOutlined,
  SafetyOutlined,
  SaveOutlined,
  StopOutlined,
  TeamOutlined,
  UserOutlined,
  WarningOutlined,
  DollarOutlined,
  CalendarOutlined,
  ThunderboltOutlined,
} from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axiosInstance from "../../api/axiosInstance";
import { useAuth } from "../../context/AuthContext";
import notify from "../../utils/notify";

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

// ─── helpers ────────────────────────────────────────────────────────────────

const fmtDate = (v) =>
  v ? new Date(v).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "—";

const slaStatusColor = (s) => {
  const v = String(s ?? "").toLowerCase();
  if (v === "active")     return "green";
  if (v === "warning")    return "gold";
  if (v === "violation")  return "red";
  if (v === "terminated") return "default";
  if (v === "pending")    return "blue";
  return "default";
};

const slaStatusIcon = (s) => {
  const v = String(s ?? "").toLowerCase();
  if (v === "active")     return <CheckCircleOutlined />;
  if (v === "warning")    return <WarningOutlined />;
  if (v === "violation")  return <ExclamationCircleOutlined />;
  if (v === "terminated") return <StopOutlined />;
  return <InfoCircleOutlined />;
};

// ─── Policy editor ───────────────────────────────────────────────────────────

const PolicySection = ({ title, icon, children }) => (
  <div style={{ marginBottom: 28 }}>
    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
      <div style={{
        width: 30, height: 30, borderRadius: 8,
        background: "#e6f4ff", display: "flex",
        alignItems: "center", justifyContent: "center", color: "var(--hms-accent)", fontSize: 14,
      }}>
        {icon}
      </div>
      <Text strong style={{ fontSize: 14 }}>{title}</Text>
    </div>
    <div style={{ paddingLeft: 38 }}>{children}</div>
  </div>
);

const FieldRow = ({ label, help, children }) => (
  <Row gutter={[16, 0]} align="middle" style={{ marginBottom: 12 }}>
    <Col xs={24} sm={10}>
      <Text style={{ fontSize: 13 }}>{label}</Text>
      {help && (
        <div style={{ fontSize: 11, color: "#8c8c8c", marginTop: 1 }}>{help}</div>
      )}
    </Col>
    <Col xs={24} sm={14}>{children}</Col>
  </Row>
);

// ─── SLA Policy defaults form ────────────────────────────────────────────────

const DEFAULT_FORM = {
  defaultMinBookingsPerMonth:    5,
  defaultMaxCancellationRate:    20,
  defaultMinGuestRating:         3.5,
  defaultMaxResponseTimeMinutes: 120,
  defaultBaseCommissionRate:     10,
  defaultBonusCommissionRate:    null,
  defaultBonusThreshold:         null,
  reviewPeriodDays:              30,
  useCustomSlaPerAgent:          false,
  slaText:                       "",
};

// ─── Main component ──────────────────────────────────────────────────────────

const HotelSlaPage = () => {
  const { authData } = useAuth();
  const qc = useQueryClient();
  const hotelId = authData?.hotelId;

  const [form, setForm]                           = useState(DEFAULT_FORM);
  const [terminateModal, setTerminateModal]       = useState({ open: false, sla: null });
  const [terminateReason, setTerminateReason]     = useState("");
  const [previewOpen, setPreviewOpen]             = useState(false);
  const [dirty, setDirty]                         = useState(false);

  const patch = (key, value) => {
    setForm((p) => ({ ...p, [key]: value }));
    setDirty(true);
  };

  // ── queries ──────────────────────────────────────────────────────────────

  const defaultsQuery = useQuery({
    queryKey: ["hotel-sla-defaults", hotelId],
    enabled: !!hotelId,
    retry: false,
    queryFn: async () => {
      const r = await axiosInstance.get(`/agent-sla/hotel/${hotelId}/defaults`);
      return r.data;
    },
  });

  useEffect(() => {
    if (!defaultsQuery.data) return;
    const d = defaultsQuery.data;
    setForm({
      defaultMinBookingsPerMonth:    d.defaultMinBookingsPerMonth    ?? 5,
      defaultMaxCancellationRate:    d.defaultMaxCancellationRate    ?? 20,
      defaultMinGuestRating:         d.defaultMinGuestRating         ?? 3.5,
      defaultMaxResponseTimeMinutes: d.defaultMaxResponseTimeMinutes ?? 120,
      defaultBaseCommissionRate:     d.defaultBaseCommissionRate     ?? 10,
      defaultBonusCommissionRate:    d.defaultBonusCommissionRate    ?? null,
      defaultBonusThreshold:         d.defaultBonusThreshold         ?? null,
      reviewPeriodDays:              d.reviewPeriodDays              ?? 30,
      useCustomSlaPerAgent:          d.useCustomSlaPerAgent          ?? false,
      slaText:                       d.slaText                       ?? "",
    });
    setDirty(false);
  }, [defaultsQuery.data]);

  const agreementsQuery = useQuery({
    queryKey: ["hotel-sla-agreements", hotelId],
    enabled: !!hotelId,
    retry: false,
    queryFn: async () => {
      const r = await axiosInstance.get(`/agent-sla/hotel/${hotelId}/agreements`);
      return Array.isArray(r.data) ? r.data : [];
    },
  });

  // ── mutations ─────────────────────────────────────────────────────────────

  const saveDefaultsMutation = useMutation({
    mutationFn: async (payload) => {
      const r = await axiosInstance.put(`/agent-sla/hotel/${hotelId}/defaults`, payload);
      return r.data;
    },
    onSuccess: () => {
      notify.success("SLA policy saved successfully.");
      setDirty(false);
      qc.invalidateQueries({ queryKey: ["hotel-sla-defaults", hotelId] });
    },
    onError: (err) => {
      notify.error(err?.response?.data?.message || "Failed to save SLA policy.");
    },
  });

  const terminateMutation = useMutation({
    mutationFn: async ({ agentProfileId, reason }) => {
      const r = await axiosInstance.post(
        `/agent-sla/hotel/${hotelId}/agents/${agentProfileId}/terminate`,
        { reason }
      );
      return r.data;
    },
    onSuccess: () => {
      notify.success("SLA agreement terminated.");
      setTerminateModal({ open: false, sla: null });
      setTerminateReason("");
      qc.invalidateQueries({ queryKey: ["hotel-sla-agreements", hotelId] });
    },
    onError: (err) => {
      notify.error(err?.response?.data?.message || "Failed to terminate SLA.");
    },
  });

  const handleSave = () => {
    if (!form.defaultBaseCommissionRate) {
      notify.warning("Base commission rate is required.");
      return;
    }
    saveDefaultsMutation.mutate(form);
  };

  // ── agreements table columns ──────────────────────────────────────────────

  const agreementColumns = [
    {
      title: "Agent",
      key: "agent",
      render: (_, row) => (
        <Space>
          <Avatar size={32} icon={<UserOutlined />} style={{ background: "var(--hms-accent)" }} />
          <div>
            <div style={{ fontWeight: 600, fontSize: 13, lineHeight: 1.3 }}>
              {row.agentName || "—"}
            </div>
            <div style={{ color: "#8c8c8c", fontSize: 11 }}>{row.agentCompany || row.agentEmail || "—"}</div>
          </div>
        </Space>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (v) => (
        <Tag color={slaStatusColor(v)} icon={slaStatusIcon(v)}>
          {v ?? "—"}
        </Tag>
      ),
    },
    {
      title: "Accepted",
      dataIndex: "agentAccepted",
      key: "agentAccepted",
      render: (v, row) =>
        v ? (
          <Space direction="vertical" size={0}>
            <Tag color="green" icon={<CheckCircleOutlined />}>Accepted</Tag>
            <Text type="secondary" style={{ fontSize: 11 }}>{fmtDate(row.agentAcceptedAtUtc)}</Text>
          </Space>
        ) : (
          <Tag color="gold" icon={<WarningOutlined />}>Pending Acceptance</Tag>
        ),
    },
    {
      title: "Commission",
      key: "commission",
      render: (_, row) => (
        <Space direction="vertical" size={0}>
          <Text strong>{row.baseCommissionRate}%</Text>
          {row.bonusCommissionRate && (
            <Text type="secondary" style={{ fontSize: 11 }}>
              +{row.bonusCommissionRate}% bonus
            </Text>
          )}
        </Space>
      ),
    },
    {
      title: "Effective From",
      dataIndex: "effectiveFrom",
      key: "effectiveFrom",
      render: (v) => <Text style={{ fontSize: 13 }}>{fmtDate(v)}</Text>,
    },
    {
      title: "",
      key: "actions",
      render: (_, row) =>
        row.status !== "Terminated" ? (
          <Tooltip title="Terminate this SLA agreement">
            <Button
              danger
              size="small"
              icon={<StopOutlined />}
              onClick={() => setTerminateModal({ open: true, sla: row })}
            >
              Terminate
            </Button>
          </Tooltip>
        ) : (
          <Tag color="default">
            Terminated {fmtDate(row.terminatedAtUtc)}
          </Tag>
        ),
    },
  ];

  // ── loading / error ───────────────────────────────────────────────────────

  if (!hotelId) {
    return (
      <Alert type="warning" message="No hotel context found." showIcon style={{ margin: 24 }} />
    );
  }

  if (defaultsQuery.isLoading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", padding: 60 }}>
        <Spin size="large" tip="Loading SLA policy…" />
      </div>
    );
  }

  // ── render ────────────────────────────────────────────────────────────────

  const agreements = agreementsQuery.data ?? [];
  const active     = agreements.filter((a) => a.status !== "Terminated");
  const terminated = agreements.filter((a) => a.status === "Terminated");

  return (
    <div style={{ padding: "8px 0" }}>
      <Space direction="vertical" size={20} style={{ width: "100%" }}>

        {/* ── Header ── */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12 }}>
          <div>
            <Space align="center" style={{ marginBottom: 4 }}>
              <Title level={3} style={{ marginBottom: 0 }}>SLA Management</Title>
              <Tag color={defaultsQuery.data ? "green" : "gold"} icon={<SafetyOutlined />}>
                {defaultsQuery.data ? "Policy Configured" : "Not Configured"}
              </Tag>
            </Space>
            <Text type="secondary">
              Define performance targets, commission structures, and the full SLA text shown to agents before they accept.
            </Text>
          </div>
          <Space wrap>
            <Button
              icon={<ReloadOutlined />}
              onClick={() => {
                qc.invalidateQueries({ queryKey: ["hotel-sla-defaults", hotelId] });
                qc.invalidateQueries({ queryKey: ["hotel-sla-agreements", hotelId] });
              }}
            >
              Refresh
            </Button>
            <Button
              icon={<FileTextOutlined />}
              onClick={() => setPreviewOpen(true)}
              disabled={!form.defaultBaseCommissionRate}
            >
              Preview Agent View
            </Button>
            <Button
              type="primary"
              icon={<SaveOutlined />}
              loading={saveDefaultsMutation.isPending}
              onClick={handleSave}
              disabled={!dirty && !!defaultsQuery.data}
            >
              {dirty ? "Save Changes" : "Policy Saved"}
            </Button>
          </Space>
        </div>

        {/* ── Tip banner ── */}
        <Alert
          type="info"
          showIcon
          icon={<InfoCircleOutlined />}
          message="Agents see these terms during their application and must explicitly accept before proceeding."
          description="Changes to this policy do not automatically update existing active agreements — only new acceptances will use the updated terms."
          style={{ borderRadius: 8 }}
        />

        <Row gutter={[20, 20]}>

          {/* ══ LEFT — Policy editor ══════════════════════════════════════════ */}
          <Col xs={24} xl={14}>
            <Card
              title={
                <Space>
                  <EditOutlined />
                  SLA Policy Configuration
                </Space>
              }
              extra={
                dirty && (
                  <Tag color="gold" icon={<ExclamationCircleOutlined />}>
                    Unsaved changes
                  </Tag>
                )
              }
              style={{ borderRadius: 12 }}
            >

              {/* ── Performance Targets ── */}
              <PolicySection title="Performance Targets" icon={<ThunderboltOutlined />}>
                <FieldRow
                  label="Minimum bookings / month"
                  help="Agent must place at least this many bookings each review period."
                >
                  <InputNumber
                    min={0}
                    style={{ width: "100%" }}
                    value={form.defaultMinBookingsPerMonth}
                    onChange={(v) => patch("defaultMinBookingsPerMonth", v)}
                    addonAfter="bookings"
                  />
                </FieldRow>
                <FieldRow
                  label="Maximum cancellation rate"
                  help="Percentage of bookings that may be cancelled before a violation is triggered."
                >
                  <InputNumber
                    min={0}
                    max={100}
                    step={0.5}
                    style={{ width: "100%" }}
                    value={form.defaultMaxCancellationRate}
                    onChange={(v) => patch("defaultMaxCancellationRate", v)}
                    addonAfter="%"
                  />
                </FieldRow>
                <FieldRow
                  label="Minimum guest rating"
                  help="Average star rating (1–5) required from guests booking through this agent."
                >
                  <InputNumber
                    min={1}
                    max={5}
                    step={0.1}
                    style={{ width: "100%" }}
                    value={form.defaultMinGuestRating}
                    onChange={(v) => patch("defaultMinGuestRating", v)}
                    addonAfter="★ / 5"
                  />
                </FieldRow>
                <FieldRow
                  label="Maximum response time"
                  help="Maximum minutes the agent may take to respond to guest queries."
                >
                  <InputNumber
                    min={0}
                    style={{ width: "100%" }}
                    value={form.defaultMaxResponseTimeMinutes}
                    onChange={(v) => patch("defaultMaxResponseTimeMinutes", v)}
                    addonAfter="min"
                  />
                </FieldRow>
              </PolicySection>

              <Divider style={{ margin: "0 0 24px" }} />

              {/* ── Commission Structure ── */}
              <PolicySection title="Commission Structure" icon={<DollarOutlined />}>
                <FieldRow
                  label="Base commission rate"
                  help="Standard commission earned on every completed booking."
                >
                  <InputNumber
                    min={0}
                    max={100}
                    step={0.5}
                    style={{ width: "100%" }}
                    value={form.defaultBaseCommissionRate}
                    onChange={(v) => patch("defaultBaseCommissionRate", v)}
                    addonAfter="%"
                  />
                </FieldRow>
                <FieldRow
                  label="Bonus commission rate"
                  help="Extra commission applied when the bonus threshold is exceeded. Leave blank to disable."
                >
                  <InputNumber
                    min={0}
                    max={100}
                    step={0.5}
                    style={{ width: "100%" }}
                    value={form.defaultBonusCommissionRate}
                    onChange={(v) => patch("defaultBonusCommissionRate", v || null)}
                    placeholder="Optional"
                    addonAfter="%"
                  />
                </FieldRow>
                {form.defaultBonusCommissionRate && (
                  <FieldRow
                    label="Bonus threshold"
                    help="Number of bookings per month required to unlock the bonus rate."
                  >
                    <InputNumber
                      min={0}
                      style={{ width: "100%" }}
                      value={form.defaultBonusThreshold}
                      onChange={(v) => patch("defaultBonusThreshold", v || null)}
                      placeholder="e.g. 15"
                      addonAfter="bookings"
                    />
                  </FieldRow>
                )}
              </PolicySection>

              <Divider style={{ margin: "0 0 24px" }} />

              {/* ── Review Cycle ── */}
              <PolicySection title="Review Cycle" icon={<CalendarOutlined />}>
                <FieldRow
                  label="Review period"
                  help="How often performance is measured against the targets above."
                >
                  <Select
                    style={{ width: "100%" }}
                    value={form.reviewPeriodDays}
                    onChange={(v) => patch("reviewPeriodDays", v)}
                    options={[
                      { value: 30,  label: "Monthly (30 days)" },
                      { value: 60,  label: "Every 60 days" },
                      { value: 90,  label: "Quarterly (90 days)" },
                      { value: 180, label: "Semi-annually (180 days)" },
                      { value: 365, label: "Annually (365 days)" },
                    ]}
                  />
                </FieldRow>
                <FieldRow
                  label="Per-agent custom SLA"
                  help="Allow overriding these defaults per individual agent agreement."
                >
                  <Switch
                    checked={form.useCustomSlaPerAgent}
                    onChange={(v) => patch("useCustomSlaPerAgent", v)}
                  />
                  <Text type="secondary" style={{ fontSize: 12, marginLeft: 8 }}>
                    {form.useCustomSlaPerAgent ? "Enabled" : "Disabled"}
                  </Text>
                </FieldRow>
              </PolicySection>

              <Divider style={{ margin: "0 0 24px" }} />

              {/* ── Full SLA Text ── */}
              <PolicySection title="Full SLA Policy Text" icon={<FileTextOutlined />}>
                <Alert
                  type="info"
                  showIcon={false}
                  message={
                    <Text style={{ fontSize: 12 }}>
                      This text is shown in full to agents during the application. Describe termination
                      conditions, dispute procedures, confidentiality obligations, and any other terms
                      important to your hotel.
                    </Text>
                  }
                  style={{ marginBottom: 10, borderRadius: 6 }}
                />
                <TextArea
                  rows={10}
                  value={form.slaText}
                  onChange={(e) => patch("slaText", e.target.value)}
                  placeholder={`Example:\n\n1. SCOPE\nThis agreement governs the relationship between [Hotel Name] and the appointed travel agent for the purpose of hotel room bookings.\n\n2. OBLIGATIONS\nThe Agent shall maintain the minimum booking volumes and quality standards stated in the Performance Targets section above.\n\n3. TERMINATION\nEither party may terminate this agreement with 30 days written notice. The Hotel reserves the right to terminate immediately for material breach of targets.\n\n4. CONFIDENTIALITY\nAll commission rates and booking data shared between the parties shall remain confidential.`}
                  style={{ borderRadius: 8, fontFamily: "monospace", fontSize: 13 }}
                />
                <Text type="secondary" style={{ fontSize: 11, marginTop: 6, display: "block" }}>
                  {(form.slaText ?? "").length} characters · Plain text, no markdown
                </Text>
              </PolicySection>

              {/* Save footer */}
              <div style={{
                borderTop: "1px solid #f0f0f0", paddingTop: 16, marginTop: 8,
                display: "flex", justifyContent: "flex-end", gap: 10,
              }}>
                <Button
                  onClick={() => {
                    if (defaultsQuery.data) {
                      setForm({
                        defaultMinBookingsPerMonth:    defaultsQuery.data.defaultMinBookingsPerMonth    ?? 5,
                        defaultMaxCancellationRate:    defaultsQuery.data.defaultMaxCancellationRate    ?? 20,
                        defaultMinGuestRating:         defaultsQuery.data.defaultMinGuestRating         ?? 3.5,
                        defaultMaxResponseTimeMinutes: defaultsQuery.data.defaultMaxResponseTimeMinutes ?? 120,
                        defaultBaseCommissionRate:     defaultsQuery.data.defaultBaseCommissionRate     ?? 10,
                        defaultBonusCommissionRate:    defaultsQuery.data.defaultBonusCommissionRate    ?? null,
                        defaultBonusThreshold:         defaultsQuery.data.defaultBonusThreshold         ?? null,
                        reviewPeriodDays:              defaultsQuery.data.reviewPeriodDays              ?? 30,
                        useCustomSlaPerAgent:          defaultsQuery.data.useCustomSlaPerAgent          ?? false,
                        slaText:                       defaultsQuery.data.slaText                       ?? "",
                      });
                    } else {
                      setForm(DEFAULT_FORM);
                    }
                    setDirty(false);
                  }}
                  disabled={!dirty}
                >
                  Discard Changes
                </Button>
                <Button
                  type="primary"
                  icon={<SaveOutlined />}
                  loading={saveDefaultsMutation.isPending}
                  onClick={handleSave}
                >
                  Save SLA Policy
                </Button>
              </div>
            </Card>
          </Col>

          {/* ══ RIGHT — Live agreements & summary ════════════════════════════ */}
          <Col xs={24} xl={10}>
            <Space direction="vertical" size={16} style={{ width: "100%" }}>

              {/* Stats */}
              <Row gutter={[10, 10]}>
                {[
                  {
                    label: "Active Agreements",
                    value: active.length,
                    color: "#52c41a",
                    bg: "#f6ffed",
                    border: "#b7eb8f",
                  },
                  {
                    label: "Pending Acceptance",
                    value: active.filter((a) => !a.agentAccepted).length,
                    color: "#faad14",
                    bg: "#fffbe6",
                    border: "#ffe58f",
                  },
                  {
                    label: "Terminated",
                    value: terminated.length,
                    color: "#8c8c8c",
                    bg: "#fafafa",
                    border: "#d9d9d9",
                  },
                ].map((s) => (
                  <Col xs={8} key={s.label}>
                    <Card
                      style={{
                        background: s.bg,
                        border: `1px solid ${s.border}`,
                        borderRadius: 10,
                      }}
                      bodyStyle={{ padding: "12px 14px", textAlign: "center" }}
                    >
                      <div style={{ fontSize: 22, fontWeight: 700, color: s.color, lineHeight: 1 }}>
                        {s.value}
                      </div>
                      <div style={{ fontSize: 11, color: "#8c8c8c", marginTop: 4 }}>{s.label}</div>
                    </Card>
                  </Col>
                ))}
              </Row>

              {/* Policy snapshot */}
              {defaultsQuery.data && (
                <Card
                  title={<Space><SafetyOutlined />Current Policy Snapshot</Space>}
                  style={{ borderRadius: 10 }}
                  size="small"
                >
                  <Space direction="vertical" size={0} style={{ width: "100%" }}>
                    {[
                      { label: "Min bookings / month",    value: `${form.defaultMinBookingsPerMonth} bookings` },
                      { label: "Max cancellation rate",   value: `≤ ${form.defaultMaxCancellationRate}%` },
                      { label: "Min guest rating",        value: `≥ ${form.defaultMinGuestRating} ★` },
                      { label: "Max response time",       value: `${form.defaultMaxResponseTimeMinutes} min` },
                      { label: "Base commission",         value: `${form.defaultBaseCommissionRate}%` },
                      form.defaultBonusCommissionRate
                        ? { label: "Bonus commission",    value: `+${form.defaultBonusCommissionRate}% (≥${form.defaultBonusThreshold} bookings)` }
                        : null,
                      { label: "Review cycle",            value: `Every ${form.reviewPeriodDays} days` },
                    ]
                      .filter(Boolean)
                      .map((row) => (
                        <div key={row.label} style={{
                          display: "flex", justifyContent: "space-between",
                          padding: "7px 0", borderBottom: "1px solid #f5f5f5",
                          alignItems: "center",
                        }}>
                          <Text type="secondary" style={{ fontSize: 12 }}>{row.label}</Text>
                          <Text strong style={{ fontSize: 12 }}>{row.value}</Text>
                        </div>
                      ))}
                  </Space>
                </Card>
              )}

              {/* No policy yet */}
              {!defaultsQuery.data && !defaultsQuery.isLoading && (
                <Alert
                  type="warning"
                  showIcon
                  icon={<WarningOutlined />}
                  message="No SLA policy saved yet"
                  description="Fill in the policy form and click 'Save SLA Policy' to publish it. Agents will see these terms when applying."
                />
              )}
            </Space>
          </Col>
        </Row>

        {/* ══ Agent SLA Agreements ═════════════════════════════════════════════ */}
        <Card
          title={
            <Space>
              <TeamOutlined />
              Agent SLA Agreements
              {active.length > 0 && <Badge count={active.length} style={{ background: "#52c41a" }} />}
            </Space>
          }
          style={{ borderRadius: 12 }}
          extra={
            <Button
              size="small"
              icon={<ReloadOutlined />}
              onClick={() => qc.invalidateQueries({ queryKey: ["hotel-sla-agreements", hotelId] })}
            >
              Refresh
            </Button>
          }
        >
          {agreementsQuery.isLoading ? (
            <div style={{ textAlign: "center", padding: 32 }}>
              <Spin />
            </div>
          ) : agreements.length === 0 ? (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="No SLA agreements yet. Agreements are created automatically when an agent accepts your policy during their application."
            />
          ) : (
            <Table
              rowKey="agentSlaId"
              columns={agreementColumns}
              dataSource={agreements}
              pagination={{ pageSize: 8 }}
              size="small"
              rowClassName={(row) =>
                row.status === "Terminated" ? "terminated-row" : ""
              }
            />
          )}
        </Card>
      </Space>

      {/* ── Terminate modal ── */}
      <Modal
        title={
          <Space>
            <StopOutlined style={{ color: "#ff4d4f" }} />
            Terminate SLA Agreement
          </Space>
        }
        open={terminateModal.open}
        onOk={() =>
          terminateMutation.mutate({
            agentProfileId: terminateModal.sla?.agentProfileId,
            reason: terminateReason,
          })
        }
        onCancel={() => {
          setTerminateModal({ open: false, sla: null });
          setTerminateReason("");
        }}
        okText="Terminate Agreement"
        cancelText="Cancel"
        okButtonProps={{ danger: true, loading: terminateMutation.isPending }}
      >
        <Space direction="vertical" size={12} style={{ width: "100%" }}>
          <Text>
            You are about to terminate the SLA agreement with{" "}
            <Text strong>{terminateModal.sla?.agentName ?? "this agent"}</Text>.
          </Text>
          <Alert
            type="error"
            showIcon
            message="This will revoke the agent's approved affiliation immediately. Their KYC data is preserved."
          />
          <div>
            <Text strong>Reason for termination</Text>
            <TextArea
              rows={3}
              style={{ marginTop: 6 }}
              value={terminateReason}
              onChange={(e) => setTerminateReason(e.target.value)}
              placeholder="Performance targets not met, fraudulent activity, mutual agreement…"
            />
            <Text type="secondary" style={{ fontSize: 11 }}>
              This reason will be visible to the agent in their portal.
            </Text>
          </div>
        </Space>
      </Modal>

      {/* ── Agent preview modal ── */}
      <Modal
        title={
          <Space>
            <FileTextOutlined />
            Agent View Preview — SLA Terms
          </Space>
        }
        open={previewOpen}
        onCancel={() => setPreviewOpen(false)}
        footer={
          <Button type="primary" onClick={() => setPreviewOpen(false)}>Close</Button>
        }
        width={560}
      >
        <Space direction="vertical" size={12} style={{ width: "100%" }}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            This is exactly what an agent sees when reviewing your SLA before accepting.
          </Text>

          {/* Commission highlight */}
          <div style={{
            padding: "14px 16px",
            background: "#e6f4ff",
            borderRadius: 8,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}>
            <div>
              <div style={{ fontSize: 12, color: "var(--hms-accent)", fontWeight: 500 }}>Base Commission</div>
              <div style={{ fontSize: 26, fontWeight: 800, color: "var(--hms-accent)", lineHeight: 1 }}>
                {form.defaultBaseCommissionRate}%
              </div>
            </div>
            {form.defaultBonusCommissionRate && (
              <div style={{ textAlign: "right" }}>
                <div style={{ fontSize: 12, color: "#52c41a", fontWeight: 500 }}>Bonus Commission</div>
                <div style={{ fontSize: 20, fontWeight: 700, color: "#52c41a" }}>
                  +{form.defaultBonusCommissionRate}%
                </div>
                <div style={{ fontSize: 11, color: "#8c8c8c" }}>
                  when ≥ {form.defaultBonusThreshold} bookings
                </div>
              </div>
            )}
          </div>

          {/* Targets */}
          <div style={{ background: "#fafafa", borderRadius: 8, padding: "10px 14px" }}>
            <Text strong style={{ fontSize: 12, textTransform: "uppercase",
              letterSpacing: "0.04em", color: "#8c8c8c", display: "block", marginBottom: 8 }}>
              Performance Targets
            </Text>
            {[
              { label: "Min bookings / month",  value: `${form.defaultMinBookingsPerMonth} bookings` },
              { label: "Max cancellation rate", value: `≤ ${form.defaultMaxCancellationRate}%` },
              { label: "Min guest rating",      value: `≥ ${form.defaultMinGuestRating} ★` },
              { label: "Max response time",     value: `${form.defaultMaxResponseTimeMinutes} min` },
              { label: "Review cycle",          value: `Every ${form.reviewPeriodDays} days` },
            ].map((r) => (
              <div key={r.label} style={{
                display: "flex", justifyContent: "space-between",
                padding: "5px 0", borderBottom: "1px solid #f0f0f0",
                fontSize: 13,
              }}>
                <Text type="secondary">{r.label}</Text>
                <Text strong>{r.value}</Text>
              </div>
            ))}
          </div>

          {/* SLA text */}
          {form.slaText && (
            <div style={{
              border: "1px solid #d9d9d9", borderRadius: 8, padding: "12px 14px",
              maxHeight: 220, overflowY: "auto",
              fontFamily: "monospace", fontSize: 12, color: "#595959",
              whiteSpace: "pre-wrap", lineHeight: 1.6,
            }}>
              {form.slaText}
            </div>
          )}

          {/* Acceptance checkbox preview */}
          <div style={{
            padding: "12px 14px", borderRadius: 8,
            border: "1px solid #d9d9d9", background: "#fff",
          }}>
            <Space align="start">
              <div style={{
                width: 14, height: 14, borderRadius: 3, border: "2px solid var(--hms-accent)",
                background: "var(--hms-accent)", display: "flex", alignItems: "center",
                justifyContent: "center", flexShrink: 0, marginTop: 2,
              }}>
                <CheckCircleOutlined style={{ fontSize: 8, color: "#fff" }} />
              </div>
              <Text style={{ fontSize: 12 }}>
                I have read and accept the SLA terms, including the performance targets
                and commission structure outlined above. I understand that the hotel may
                terminate this agreement if targets are not met.
              </Text>
            </Space>
          </div>
        </Space>
      </Modal>

      <style>{`
        .terminated-row { opacity: 0.55; }
      `}</style>
    </div>
  );
};

export default HotelSlaPage;